// =============================================================================
// Concierge AI — API Client
// =============================================================================
// Typed fetch wrapper for Concierge Worker API.
// Proxied through Vite dev server → localhost:8787 (wrangler dev).
// =============================================================================

import { useAuthStore } from "@/stores/auth";

const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : "/api";

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const { token, isSessionExpired, logout, touchActivity } = useAuthStore.getState();

  // Check session timeout before making request
  if (isSessionExpired()) {
    logout();
    throw new ApiError(401, "Session expired — please log in again");
  }

  const res = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (res.status === 401) {
    logout();
    throw new ApiError(401, "Unauthorized — session ended");
  }

  if (!res.ok) {
    const body = await res.text().catch(() => "Unknown error");
    throw new ApiError(res.status, body);
  }

  touchActivity();
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

import type {
  Tenant,
  Customer,
  Transaction,
  DashboardStats,
  ChannelConfig,
  Analytics,
  TenantSettings,
  Conversation,
  Message,
  KBDocument,
  KBDocumentCategory,
  FAQEntry,
  PaginatedResult,
  LeadPipeline,
  LeadStage,
  KBConnector,
  ConnectorProvider,
  SyncFrequency,
  ProviderMeta,
  SyncResult,
  WorkflowItem,
  ApprovalItem,
  ApprovalStats,
} from "@/lib/types";

/** Shared helper to build query-string from optional params */
function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== "") sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export const api = {
  // --- Onboarding ---
  createTenant: (payload: unknown) =>
    request<{ ok: boolean; tenantId: string }>("/onboarding/tenants", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  // --- Tenants ---
  listTenants: () =>
    request<{ tenants: Tenant[] }>("/admin/tenants"),

  getTenant: (tenantId: string) =>
    request<Tenant>(`/admin/tenants/${encodeURIComponent(tenantId)}`),

  updateTenant: (tenantId: string, payload: { name?: string; adminEmail?: string; adminPhone?: string }) =>
    request<{ ok: boolean }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}`,
      { method: "PUT", body: JSON.stringify(payload) },
    ),

  upgradePlan: (tenantId: string, plan: string) =>
    request<{ ok: boolean; plan: string }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/plan`,
      { method: "PUT", body: JSON.stringify({ plan }) },
    ),

  // --- Tenant Settings (assistant config) ---
  getTenantSettings: (tenantId: string) =>
    request<TenantSettings>(`/admin/tenants/${encodeURIComponent(tenantId)}/settings`),

  updateTenantSettings: (tenantId: string, payload: Partial<TenantSettings>) =>
    request<{ ok: boolean }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/settings`,
      { method: "PUT", body: JSON.stringify(payload) },
    ),

  // --- Dashboard ---
  getDashboardStats: (tenantId: string) =>
    request<DashboardStats>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/dashboard`,
    ),

  // --- Customers ---
  listCustomers: (tenantId: string, params?: { search?: string; cursor?: string; limit?: number }) =>
    request<PaginatedResult<Customer>>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/customers${qs({ search: params?.search, cursor: params?.cursor, limit: params?.limit })}`,
    ),

  // --- Conversations ---
  listConversations: (tenantId: string, params?: { status?: string; search?: string; cursor?: string; limit?: number }) =>
    request<PaginatedResult<Conversation>>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/conversations${qs({ status: params?.status, search: params?.search, cursor: params?.cursor, limit: params?.limit })}`,
    ),

  getConversation: (tenantId: string, conversationId: string) =>
    request<{ conversation: Conversation; messages: Message[] }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/conversations/${encodeURIComponent(conversationId)}`,
    ),

  // --- Knowledge Base ---
  listKBDocuments: (tenantId: string, params?: { category?: KBDocumentCategory; search?: string; cursor?: string; limit?: number }) =>
    request<PaginatedResult<KBDocument>>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/kb${qs({ category: params?.category, search: params?.search, cursor: params?.cursor, limit: params?.limit })}`,
    ),

  deleteKBDocument: (tenantId: string, docId: string) =>
    request<{ ok: boolean }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/kb/${encodeURIComponent(docId)}`,
      { method: "DELETE" },
    ),

  uploadKBDocument: async (tenantId: string, file: File, category: string): Promise<{ ok: boolean }> => {
    const url = `${API_BASE}/admin/tenants/${encodeURIComponent(tenantId)}/kb`;
    const token = useAuthStore.getState().token;
    const form = new FormData();
    form.append("file", file);
    form.append("category", category);
    const res = await fetch(url, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => "Upload failed"));
    return res.json();
  },

  listFAQs: (tenantId: string, params?: { search?: string; cursor?: string; limit?: number }) =>
    request<PaginatedResult<FAQEntry>>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/faqs${qs({ search: params?.search, cursor: params?.cursor, limit: params?.limit })}`,
    ),

  createFAQ: (tenantId: string, payload: { question: string; answer: string }) =>
    request<{ ok: boolean; faq: FAQEntry }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/faqs`,
      { method: "POST", body: JSON.stringify(payload) },
    ),

  updateFAQ: (tenantId: string, faqId: string, payload: { question: string; answer: string }) =>
    request<{ ok: boolean }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/faqs/${encodeURIComponent(faqId)}`,
      { method: "PUT", body: JSON.stringify(payload) },
    ),

  deleteFAQ: (tenantId: string, faqId: string) =>
    request<{ ok: boolean }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/faqs/${encodeURIComponent(faqId)}`,
      { method: "DELETE" },
    ),

  // --- KB Connectors ---
  listConnectors: (tenantId: string) =>
    request<{ connectors: KBConnector[]; providers: Record<ConnectorProvider, ProviderMeta> }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/kb/connectors`,
    ),

  createConnector: (tenantId: string, payload: { provider: ConnectorProvider; name: string; config: Record<string, unknown>; syncFrequency?: SyncFrequency }) =>
    request<{ ok: boolean; connector: KBConnector }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/kb/connectors`,
      { method: "POST", body: JSON.stringify(payload) },
    ),

  updateConnector: (tenantId: string, connectorId: string, payload: { name?: string; config?: Record<string, unknown>; syncFrequency?: SyncFrequency; status?: string }) =>
    request<{ ok: boolean }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/kb/connectors/${encodeURIComponent(connectorId)}`,
      { method: "PUT", body: JSON.stringify(payload) },
    ),

  deleteConnector: (tenantId: string, connectorId: string) =>
    request<{ ok: boolean }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/kb/connectors/${encodeURIComponent(connectorId)}`,
      { method: "DELETE" },
    ),

  syncConnector: (tenantId: string, connectorId: string) =>
    request<{ ok: boolean; result: SyncResult }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/kb/connectors/${encodeURIComponent(connectorId)}/sync`,
      { method: "POST" },
    ),

  // --- Payments ---
  listTransactions: (tenantId: string, params?: { status?: string; from?: string; to?: string; cursor?: string; limit?: number }) =>
    request<PaginatedResult<Transaction>>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/payments${qs({ status: params?.status, from: params?.from, to: params?.to, cursor: params?.cursor, limit: params?.limit })}`,
    ),

  // --- Channels ---
  listChannels: (tenantId: string) =>
    request<{ channels: ChannelConfig[]; tenantSlug: string }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/channels`,
    ),

  createChannel: (tenantId: string, payload: { channelType: string; identifier: string }) =>
    request<{ ok: boolean; channel: { id: string } }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/channels`,
      { method: "POST", body: JSON.stringify(payload) },
    ),

  updateChannel: (tenantId: string, channelId: string, payload: { status?: string }) =>
    request<{ ok: boolean }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/channels/${encodeURIComponent(channelId)}`,
      { method: "PUT", body: JSON.stringify(payload) },
    ),

  // --- Analytics ---
  getAnalytics: (tenantId: string, period = "30d") =>
    request<Analytics>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/analytics?period=${encodeURIComponent(period)}`,
    ),

  // --- Lead Pipeline ---
  getLeadPipeline: (tenantId: string) =>
    request<LeadPipeline>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/leads`,
    ),

  updateLeadStage: (tenantId: string, leadId: string, stage: LeadStage) =>
    request<{ ok: boolean; leadId: string; stage: string }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/leads/${encodeURIComponent(leadId)}/stage`,
      { method: "PUT", body: JSON.stringify({ stage }) },
    ),

  // --- Workflows ---
  listWorkflows: (tenantId: string) =>
    request<{ workflows: WorkflowItem[] }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/workflows`,
    ),

  // --- Approvals ---
  listApprovals: (tenantId: string, params?: { status?: string; cursor?: string; limit?: number }) =>
    request<{ approvals: ApprovalItem[]; stats: ApprovalStats }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/approvals${qs({ status: params?.status, cursor: params?.cursor, limit: params?.limit })}`,
    ),

  decideApproval: (tenantId: string, approvalId: string, decision: { action: "approve" | "reject"; comment?: string }) =>
    request<{ ok: boolean }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/approvals/${encodeURIComponent(approvalId)}/decide`,
      { method: "POST", body: JSON.stringify(decision) },
    ),

  // --- Billing (Paystack) ---
  initializeBilling: (tenantId: string, plan: string) =>
    request<{ ok: boolean; accessCode: string; reference: string; authorizationUrl: string }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/billing/initialize`,
      { method: "POST", body: JSON.stringify({ plan }) },
    ),

  verifyBilling: (tenantId: string, reference: string) =>
    request<{ ok: boolean; status: string; plan?: string }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/billing/verify/${encodeURIComponent(reference)}`,
    ),

  // --- Reconciliation ---
  getReconciliationSummary: (tenantId: string) =>
    request<{ stats: { reconciliation_status: string; count: number }[]; openDisputes: number }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/reconciliation/summary`,
    ),

  getReconciliationDisputes: (tenantId: string, status = "open") =>
    request<{ disputes: { id: string; transaction_id: string; dispute_type: string; amount: number; currency: string; status: string; created_at: string }[] }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/reconciliation/disputes?status=${encodeURIComponent(status)}`,
    ),

  resolveDispute: (tenantId: string, disputeId: string, payload: { resolution: string; bookingId?: string }) =>
    request<{ ok: boolean }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/reconciliation/disputes/${encodeURIComponent(disputeId)}/resolve`,
      { method: "POST", body: JSON.stringify(payload) },
    ),

  // --- Chat ---
  sendChatMessage: (slug: string, message: string, sessionId?: string | null) =>
    request<{ reply?: string }>(
      `/chat/${encodeURIComponent(slug)}`,
      { method: "POST", body: JSON.stringify({ message, sessionId: sessionId ?? null, visitorName: "Admin" }) },
    ),
};
